import React, { useCallback, useMemo, useState } from 'react';
import { Triangle, AudioWaveform, Activity, Trophy } from 'lucide-react';
import { useSamuelLab } from '../../lib/samuel/useSamuelLab';
import { PHONES, symbolForCard } from '../../lib/vocalTract/articulations';
import { dialectSymbol } from '../../lib/vocalTract/lexicon';
import { postureFrame } from '../../lib/samuel/phonetic';
import { TractStage } from './TractStage';
import { InputPanel } from './InputPanel';
import { TransportPanel } from './TransportPanel';
import { TrajectoryChart } from './TrajectoryChart';
import { SpectroPanel } from './SpectroPanel';
import { VowelTrapezoid } from './VowelTrapezoid';
import { CompareSteve } from './CompareSteve';
import { PhoneticBuilder } from './PhoneticBuilder';

const TABS = [
  ['trapezoid', 'Trapezio vocalico', Triangle],
  ['spectro', 'Spettrogramma + IPA', AudioWaveform],
  ['traj', 'Traiettorie', Activity],
  ['compare', 'Confronto con Steve', Trophy],
];

export const SamuelLab = ({ dialect = 'AmE', phonemeIpa = '', words = [], steveClips = [], compact = false }) => {
  const g2pDialect = dialect === 'RP' ? 'RP' : 'GA';
  const lab = useSamuelLab({ dialect: g2pDialect });
  const [tab, setTab] = useState('trapezoid');
  const target = useMemo(() => { const b = symbolForCard(phonemeIpa); return b ? dialectSymbol(b, dialect) : null; }, [phonemeIpa, dialect]);
  const response = lab.responseOf(lab.current);
  const fi = lab.current?.analysis ? Math.min(lab.current.analysis.formants.length - 1, Math.floor(lab.frac * lab.current.analysis.formants.length)) : -1;
  const liveFormant = fi >= 0 ? lab.current.analysis.formants[fi] : null;

  const onReady = useCallback(() => {
    const s = target && (PHONES[target]?.s || PHONES[PHONES[target]?.from]?.s);
    if (s) lab.eng.pose(postureFrame(s), false);
  }, [target, lab.eng]);

  return (
    <div className="rounded-3xl border border-cyan-500/20 bg-slate-950/70 p-4 md:p-6 space-y-5" data-testid="samuel-lab">
      <div className="flex flex-wrap items-baseline gap-3">
        <p className="text-[10px] tracking-[0.25em] uppercase text-cyan-400/80">Vocal Tract Lab · Samuel × VocalFitness</p>
        {target && <span className="text-sm text-white font-semibold" data-testid="samuel-target">Fonema della scheda: <span className="font-mono text-amber-300">/{target}/</span></span>}
        <span className="text-xs text-slate-400">{g2pDialect === 'RP' ? 'British RP' : 'General American'}</span>
      </div>
      <div className={`grid grid-cols-1 gap-5 ${compact ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]' : 'lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]'}`}>
        <div className="space-y-4 order-2 lg:order-1 min-w-0">
          <InputPanel lab={lab} steveClips={steveClips} />
          <TransportPanel lab={lab} />
          <PhoneticBuilder lab={lab} target={target} words={words} dialect={dialect} />
        </div>
        <div className="order-1 lg:order-2 min-w-0"><TractStage lab={lab} compact={compact} onReady={onReady} /></div>
      </div>
      <div>
        <div className="flex flex-wrap gap-1 rounded-full bg-slate-900 border border-cyan-500/20 p-1 w-fit" data-testid="samuel-analysis-tabs">
          {TABS.map(([id, label, I]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`samuel-tab-${id}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${tab === id ? 'bg-cyan-400 text-slate-950' : 'text-cyan-100/70 hover:text-white'}`}>
              <I className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
        <div className="mt-3">
          {tab === 'trapezoid' && <VowelTrapezoid lab={lab} target={target} liveFormant={liveFormant} />}
          {tab === 'spectro' && <SpectroPanel clip={lab.current} response={response} frac={lab.frac} onSeek={lab.scrubTo} />}
          {tab === 'traj' && <TrajectoryChart response={response} frac={lab.frac} />}
          {tab === 'compare' && <CompareSteve lab={lab} steveClips={steveClips} />}
        </div>
      </div>
      <p className="text-[10px] text-slate-500 leading-relaxed">
        Modello neurale <a className="underline" href="https://github.com/vvolhejn/samuel" target="_blank" rel="noopener noreferrer">Samuel</a> di Václav Volhejn (GPL-3.0, eseguito nel tuo browser) su
        Pink Trombone di Neil Thapen (versione programmabile di Zack Qattan). Modalità fonetica, trapezio, IPA allineato e confronto: VocalFitness.
      </p>
    </div>
  );
};
